import { useMemo, useState } from 'react';

import {
  proposeCollapse,
  valueCountsOf,
} from '@sae/automation';
import type { CollapseRule, IndicatorRule } from '@sae/automation';

import { fmtCv, fmtNumber, fmtPct } from '../domain/format.js';
import { blockingQcIssues, pathForKind, runBlocker } from '../domain/store.js';
import type { CovariateMode, DisclosurePresetId } from '../domain/types.js';
import { useProjectState, useStore } from './hooks.js';
import { RunProgress } from './RunProgress.js';

type ValueChoice = 'yes' | 'no' | 'missing';

/** The indicator rule builder for one column (brief AUT-03). */
function RuleBuilder({
  column,
  rule,
  onApply,
}: {
  column: string;
  rule: IndicatorRule;
  onApply: (rule: IndicatorRule) => void;
}) {
  const state = useProjectState();
  const table = state.source.table;
  const values = useMemo(
    () => (table === null ? [] : valueCountsOf(table, column)),
    [table, column],
  );
  const initial = (): Record<string, ValueChoice> => {
    const map: Record<string, ValueChoice> = {};
    for (const v of rule.yesValues) map[String(v)] = 'yes';
    for (const v of rule.noValues) map[String(v)] = 'no';
    for (const v of rule.missingValues) map[String(v)] = 'missing';
    return map;
  };
  const [choices, setChoices] = useState<Record<string, ValueChoice>>(initial);

  const apply = () => {
    const yes: (number | string)[] = [];
    const no: (number | string)[] = [];
    const missing: (number | string)[] = [];
    for (const vc of values) {
      const choice = choices[String(vc.value)];
      if (choice === 'yes') yes.push(vc.value);
      else if (choice === 'no') no.push(vc.value);
      else if (choice === 'missing') missing.push(vc.value);
    }
    onApply({
      id: column,
      sourceColumn: column,
      ...(rule.label !== undefined ? { label: rule.label } : {}),
      yesValues: yes,
      noValues: no,
      missingValues: missing,
      origin: 'user',
      confidence: 1,
      reasons: ['Set in the rule builder.'],
      uncoveredValues: values
        .filter((vc) => choices[String(vc.value)] === undefined)
        .map((vc) => vc.value),
      complete: values.every(
        (vc) => choices[String(vc.value)] !== undefined,
      ),
    });
  };

  return (
    <div className="rule-builder">
      <p className="sub">
        Mark every value as yes, no, or missing. Unmarked values are
        treated as missing and counted in the run report.
      </p>
      {values.slice(0, 30).map((vc) => (
        <div key={String(vc.value)} className="rule-row">
          <span className="rule-value">
            <b>{String(vc.value)}</b>
            {vc.label !== undefined && <span> — {vc.label}</span>}
            <span className="rule-count"> · {vc.count} rows</span>
          </span>
          <span className="toggle-row small">
            {(['yes', 'no', 'missing'] as ValueChoice[]).map((choice) => (
              <button
                key={choice}
                className={`toggle${choices[String(vc.value)] === choice ? ' on' : ''}`}
                onClick={() =>
                  setChoices((c) => ({ ...c, [String(vc.value)]: choice }))
                }
              >
                {choice === 'yes' ? 'Yes' : choice === 'no' ? 'No' : 'Missing'}
              </button>
            ))}
          </span>
        </div>
      ))}
      {values.length > 30 && (
        <p className="sub">
          This column has {values.length} distinct values; only the first
          thirty are shown. A rule is complete only when every value is
          marked.
        </p>
      )}
      <button className="btn btn-primary" onClick={apply}>
        Apply Rule
      </button>
    </div>
  );
}

export function Step2Configure() {
  const store = useStore();
  const state = useProjectState();
  const setup = state.setup;
  const [editingRule, setEditingRule] = useState<string | null>(null);

  // Collapse proposals are pure functions of the table; compute them
  // once per table/setup instead of on every render.
  const collapseProposals = useMemo(() => {
    const out: Record<string, CollapseRule | null> = {};
    const table = state.source.table;
    if (table !== null && setup !== null) {
      for (const variable of setup.roles.subgroups) {
        out[variable] = proposeCollapse(table, variable);
      }
    }
    return out;
  }, [state.source.table, setup]);

  if (setup === null) {
    return (
      <div className="panel active">
        <div className="card">
          <h2>Load a File First</h2>
          <p className="sub">
            Model setup starts once a file has been profiled in Step 1.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => store.goToStep('upload')}
          >
            Go to Step 1
          </button>
        </div>
      </div>
    );
  }

  const path = pathForKind(setup.assessment.classification.kind);
  const config = state.config;
  const blocker = runBlocker(state);
  const blocking = blockingQcIssues(state);
  const run = state.run;

  const covariateNames =
    state.covariatesSource?.roles !== null &&
    state.covariatesSource?.roles !== undefined &&
    state.covariatesSource.roles.covariates.length > 0
      ? state.covariatesSource.roles.covariates
      : setup.roles.covariates;

  const feasibility = state.feasibility;
  const fitCell =
    run.result?.cells.find((c) => c.status === 'fitted') ?? run.result?.cells[0];
  const fitDomains =
    fitCell !== undefined
      ? (run.result?.domains.filter(
          (d) => d.domain.cellId === fitCell.cellId,
        ) ?? [])
      : [];
  const gammas = (fitCell?.fit?.areas ?? []).map((a) => a.gamma);

  return (
    <div className="panel active">
      <div className="grid2">
        <div className="card">
          <h2>Model Setup</h2>
          <p className="sub">
            Fay–Herriot area-level model. Choices update the preview on
            the right. Nothing is fitted until you run the model.
          </p>

          {path === 'area-level' ? (
            <>
              <div className="field">
                <label htmlFor="outcome-name">Outcome</label>
                <input
                  id="outcome-name"
                  type="text"
                  value={setup.areaIndicatorId}
                  onChange={(e) => store.setAreaIndicatorId(e.target.value)}
                />
                <p className="sub">
                  Estimate column: <strong>{setup.roles.estimate}</strong> ·
                  Variance:{' '}
                  <strong>
                    {setup.roles.variance ?? setup.roles.standardError}
                  </strong>{' '}
                  · Sample size: <strong>{setup.roles.sampleSize}</strong>
                </p>
              </div>
              <div className="field">
                <label htmlFor="estimate-scale">Estimates Are Stored As</label>
                <select
                  id="estimate-scale"
                  value={setup.estimateScale}
                  onChange={(e) =>
                    store.setEstimateScale(
                      e.target.value as 'proportion' | 'percent',
                    )
                  }
                >
                  <option value="proportion">Proportions (0 to 1)</option>
                  <option value="percent">Percents (0 to 100)</option>
                </select>
              </div>
            </>
          ) : (
            <>
              <div className="field">
                <label>Indicators to Estimate</label>
                <div className="checks">
                  {setup.roles.indicators.map((column) => {
                    const rule = setup.indicatorRules[column];
                    const selected =
                      rule !== undefined &&
                      setup.selectedIndicators.includes(rule.id);
                    return (
                      <div key={column} className="indicator-block">
                        <label className="check">
                          <input
                            type="checkbox"
                            checked={selected}
                            disabled={rule === undefined}
                            onChange={(e) =>
                              rule !== undefined &&
                              store.setIndicatorSelected(
                                rule.id,
                                e.target.checked,
                              )
                            }
                          />
                          <span>
                            <b>{rule?.label ?? column}</b>
                            <span>
                              {rule === undefined
                                ? 'No rule yet. Assign the indicator role in Step 1.'
                                : `Yes: ${rule.yesValues.join(', ') || '—'} · No: ${rule.noValues.join(', ') || '—'} · Missing: ${rule.missingValues.join(', ') || '—'}${rule.complete ? '' : ' · some values unmarked'}`}
                            </span>
                          </span>
                        </label>
                        {rule !== undefined && (
                          <button
                            className="linklike"
                            onClick={() =>
                              setEditingRule(
                                editingRule === column ? null : column,
                              )
                            }
                          >
                            {editingRule === column
                              ? 'Close the Rule Builder'
                              : 'Edit Rule'}
                          </button>
                        )}
                        {rule !== undefined && editingRule === column && (
                          <RuleBuilder
                            column={column}
                            rule={rule}
                            onApply={(r) => {
                              store.setIndicatorRule(r);
                              setEditingRule(null);
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                {setup.roles.indicators.length === 0 && (
                  <p className="sub">
                    No indicator columns yet. Assign the indicator role to
                    a column in Step 1.
                  </p>
                )}
              </div>

              <div className="field">
                <label>Subgroup Breakdown</label>
                <div className="checks">
                  {setup.roles.subgroups.map((variable) => {
                    const accepted = setup.collapses[variable];
                    const proposal = collapseProposals[variable] ?? null;
                    const shown = accepted ?? proposal;
                    return (
                      <div key={variable} className="indicator-block">
                        <label className="check">
                          <input
                            type="checkbox"
                            checked={setup.subgroupsSelected.includes(
                              variable,
                            )}
                            onChange={(e) =>
                              store.setSubgroupSelected(
                                variable,
                                e.target.checked,
                              )
                            }
                          />
                          <span>
                            <b>{variable}</b>
                            <span>
                              {shown === null
                                ? 'No sparse levels to collapse.'
                                : shown.combinedValues.length === 0
                                  ? `${shown.groups.length} levels, none sparse.`
                                  : `${shown.combinedValues.length} sparse level${shown.combinedValues.length === 1 ? '' : 's'} combined into “${shown.combinedLabel}” (${accepted !== undefined ? 'accepted' : 'proposed'}).`}
                            </span>
                          </span>
                        </label>
                        {proposal !== null &&
                          proposal.combinedValues.length > 0 &&
                          (accepted !== undefined ? (
                            <button
                              className="linklike"
                              onClick={() => store.setCollapse(variable, null)}
                            >
                              Use the Original Levels Instead
                            </button>
                          ) : (
                            <button
                              className="linklike"
                              onClick={() =>
                                store.setCollapse(variable, proposal)
                              }
                            >
                              Accept the Proposed Collapse
                            </button>
                          ))}
                      </div>
                    );
                  })}
                </div>
                {setup.roles.subgroups.length === 0 && (
                  <p className="sub">
                    No subgroup variables detected. Assign the subgroup
                    role in Step 1.
                  </p>
                )}
              </div>
            </>
          )}

          <div className="field">
            <label>Covariate Candidates</label>
            <div className="checks">
              {covariateNames.map((name) => {
                const mode: CovariateMode =
                  setup.covariateModes[name] ?? 'auto';
                return (
                  <div key={name} className="cov-row">
                    <span>
                      <b>{name}</b>
                    </span>
                    <span className="toggle-row small">
                      {(
                        [
                          ['auto', 'Automatic'],
                          ['locked', 'Always In'],
                          ['excluded', 'Exclude'],
                        ] as [CovariateMode, string][]
                      ).map(([value, label]) => (
                        <button
                          key={value}
                          className={`toggle${mode === value ? ' on' : ''}`}
                          onClick={() => store.setCovariateMode(name, value)}
                        >
                          {label}
                        </button>
                      ))}
                    </span>
                  </div>
                );
              })}
            </div>
            {covariateNames.length === 0 && (
              <p className="sub">
                No covariate candidates. The model will fit an
                intercept-only model; adding area-level covariates usually
                improves it.
              </p>
            )}
            {state.covariatesSource !== null && (
              <p className="sub">
                Covariates come from {state.covariatesSource.filename}.
              </p>
            )}
          </div>

          <div className="field">
            <label>Run Mode</label>
            <div className="toggle-row">
              <button
                className={`toggle${config.mode === 'quick' ? ' on' : ''}`}
                onClick={() => store.setConfig({ mode: 'quick' })}
              >
                Quick: Analytic MSE
              </button>
              <button
                className={`toggle${config.mode === 'full' ? ' on' : ''}`}
                onClick={() => store.setConfig({ mode: 'full' })}
              >
                Full: Bootstrap MSE (Slower)
              </button>
            </div>
          </div>

          <div className="field">
            <label>Benchmarking</label>
            <div className="toggle-row">
              <button
                className={`toggle${!config.benchmarkingEnabled ? ' on' : ''}`}
                onClick={() =>
                  store.setConfig({ benchmarkingEnabled: false })
                }
              >
                Off
              </button>
              <button
                className={`toggle${config.benchmarkingEnabled ? ' on' : ''}`}
                onClick={() => store.setConfig({ benchmarkingEnabled: true })}
              >
                On: Align to the Statewide Direct Estimate
              </button>
            </div>
            {config.benchmarkingEnabled && (
              <div className="toggle-row" style={{ marginTop: 6 }}>
                <button
                  className={`toggle${config.benchmarkingMethod === 'logit' ? ' on' : ''}`}
                  onClick={() =>
                    store.setConfig({ benchmarkingMethod: 'logit' })
                  }
                >
                  Logit Shift
                </button>
                <button
                  className={`toggle${config.benchmarkingMethod === 'ratio' ? ' on' : ''}`}
                  onClick={() =>
                    store.setConfig({ benchmarkingMethod: 'ratio' })
                  }
                >
                  Ratio
                </button>
              </div>
            )}
          </div>

          <div className="field">
            <label>Small-Sample Bias Correction</label>
            <div className="toggle-row">
              <button
                className={`toggle${!config.transformBiasCorrection ? ' on' : ''}`}
                onClick={() =>
                  store.setConfig({ transformBiasCorrection: false })
                }
              >
                Off
              </button>
              <button
                className={`toggle${config.transformBiasCorrection ? ' on' : ''}`}
                onClick={() =>
                  store.setConfig({ transformBiasCorrection: true })
                }
              >
                On: Correct the Transformed Inputs
              </button>
            </div>
            <div className="toggle-row" style={{ marginTop: 6 }}>
              <button
                className={`toggle${config.deffBasis === 'pooled' ? ' on' : ''}`}
                onClick={() => store.setConfig({ deffBasis: 'pooled' })}
              >
                Pooled Design Effect
              </button>
              <button
                className={`toggle${config.deffBasis === 'statewide' ? ' on' : ''}`}
                onClick={() => store.setConfig({ deffBasis: 'statewide' })}
              >
                Statewide Design Effect
              </button>
            </div>
          </div>

          <div className="field">
            <label>Disclosure Preset</label>
            <div className="toggle-row">
              {(
                [
                  ['standard', 'Standard · Flag CV ≥ 30%'],
                  ['strict', 'Strict · Flag CV ≥ 20%'],
                  ['sae-standard', 'SAE Standard'],
                  ['off', 'Off'],
                ] as [DisclosurePresetId, string][]
              ).map(([value, label]) => (
                <button
                  key={value}
                  className={`toggle${config.disclosurePresetId === value ? ' on' : ''}`}
                  onClick={() =>
                    store.setConfig({ disclosurePresetId: value })
                  }
                >
                  {label}
                </button>
              ))}
            </div>
            {config.disclosurePresetId === 'standard' && (
              <p className="sub">
                The Standard preset is built on published data
                presentation standards for proportions, with two
                departures from the published standard: interval
                widths for direct estimates are the logit interval
                widths, not the standard’s Clopper-Pearson
                widths, and the preset is also applied to
                model-based estimates, which the standard does not
                cover. At about 20 respondents per area, this
                preset suppresses nearly all exported estimates
                (0.93% published with frame counts supplied,
                0.00% without).
              </p>
            )}
          </div>

          <div className="field">
            <label htmlFor="transform">Transformation (Expert)</label>
            <select
              id="transform"
              value={config.transform}
              onChange={(e) =>
                store.setConfig({
                  transform: e.target.value as typeof config.transform,
                })
              }
            >
              <option value="auto">
                Automatic (Arcsine for Proportions; Declared in the Output)
              </option>
              <option value="arcsin">Arcsine</option>
              <option value="linear">Linear (no transformation)</option>
            </select>
          </div>

          <button
            className="btn btn-orange"
            disabled={run.status === 'running'}
            onClick={() => void store.run()}
          >
            {run.status === 'running' ? 'Running…' : 'Run Model →'}
          </button>{' '}
          <button
            className="btn"
            disabled={run.result === null}
            onClick={() => store.goToStep('results')}
          >
            View Current Results
          </button>
          {run.status === 'running' && run.progress.length > 0 && (
            <RunProgress events={run.progress} />
          )}
          {run.status === 'error' && run.error !== null && (
            <p className="run-error" role="alert">{run.error}</p>
          )}
          {blocker !== null && run.status !== 'running' && (
            <p className="sub">{blocker}</p>
          )}
          {blocking.length > 0 && (
            <p className="run-error" role="alert">
              Blocking check: {blocking[0].message}
            </p>
          )}
        </div>

        <div>
          <div className="card" style={{ marginBottom: 12 }}>
            <h2>What Will Run</h2>
            <p className="sub">
              Generated from the choices above, in the form a reviewer
              would use to specify the same model.
            </p>
            <div className="code">
              {`Fay–Herriot · REML · ${config.transform === 'auto' ? 'arcsine (auto)' : config.transform} transform
Indicators: ${path === 'area-level' ? setup.areaIndicatorId : setup.selectedIndicators.join(', ') || 'none selected'}
Covariates: ${covariateNames.length === 0 ? 'intercept only' : covariateNames.join(', ')}
MSE: ${config.mode === 'quick' ? `analytic (Prasad–Rao); bootstrap for adjusted-REML cells, B = ${config.bootstrapB}, seed "${config.seed}"` : `bootstrap, B = ${config.bootstrapB}, seed "${config.seed}"`}
Benchmarking: ${config.benchmarkingEnabled ? `${config.benchmarkingMethod} shift to the statewide direct estimate` : 'off'}
Bias correction: ${config.transformBiasCorrection ? 'on' : 'off'} · Design effect: ${config.deffBasis}
Disclosure: ${config.disclosurePresetId === 'standard' ? 'standard preset' : config.disclosurePresetId === 'strict' ? 'strict (flag CV ≥ 20%)' : config.disclosurePresetId === 'sae-standard' ? 'SAE Standard preset' : 'off'}`}
            </div>
          </div>

          <div className="card" style={{ marginBottom: 12 }}>
            <h2>Feasibility Preview</h2>
            {feasibility === null ? (
              <>
                <p className="sub">
                  Before fitting, project every cell: how many areas will
                  fit, which areas have no direct estimate, and which
                  results are projected to be flagged or suppressed.
                </p>
                <button
                  className="btn"
                  onClick={() => void store.refreshFeasibility()}
                >
                  Preview This Run
                </button>
              </>
            ) : feasibility.ok === false ? (
              <p className="run-error">
                The preview could not be computed: {feasibility.blockedReason}
              </p>
            ) : (
              <>
                <div className="kv" style={{ marginTop: 0 }}>
                  <div>
                    <b>
                      {feasibility.fittedCells} of {feasibility.totalCells}
                    </b>
                    <span>Cells That Will Fit</span>
                  </div>
                  <div>
                    <b>{feasibility.insufficientCells + feasibility.refusedCells}</b>
                    <span>Cells with Too Few Areas</span>
                  </div>
                  <div>
                    <b>{feasibility.areasWithoutDirect.length}</b>
                    <span>Areas with No Direct Estimate</span>
                  </div>
                  <div>
                    <b>{feasibility.projectedSuppressedDomains}</b>
                    <span>Projected Suppressions</span>
                  </div>
                </div>
                {feasibility.areasWithoutDirect.length > 0 && (
                  <p className="sub">
                    Excluded and named in the report:{' '}
                    {feasibility.areasWithoutDirect.slice(0, 8).join(', ')}
                    {feasibility.areasWithoutDirect.length > 8
                      ? ` and ${feasibility.areasWithoutDirect.length - 8} more`
                      : ''}
                    .
                  </p>
                )}
                <p className="sub">
                  Projected statuses use the direct estimates and are
                  conservative: model estimates are usually more precise.
                </p>
                <button
                  className="btn"
                  onClick={() => void store.refreshFeasibility()}
                >
                  Refresh Preview
                </button>
              </>
            )}
          </div>

          <div className="card" style={{ marginBottom: 12 }}>
            <h2>Quality Checks</h2>
            {state.qc === null ? (
              <>
                <p className="sub">
                  Twenty checks run against the confirmed setup: weights,
                  design variables, indicator coverage, subgroup levels,
                  covariates, and disclosure minimums. A blocking problem
                  stops the run.
                </p>
                <button className="btn" onClick={() => void store.refreshQc()}>
                  Run Quality Checks
                </button>
              </>
            ) : (
              <>
                <p className="sub">
                  {
                    state.qc.filter(
                      (r) => r.outcome === 'pass' || r.issues.length === 0,
                    ).length
                  }{' '}
                  of {state.qc.length} checks clear ·{' '}
                  {state.qc.reduce((n, r) => n + r.issues.length, 0)} findings.
                  The full report is in Step 4 after a run.
                </p>
                <button className="btn" onClick={() => void store.refreshQc()}>
                  Re-Run Checks
                </button>
              </>
            )}
          </div>

          {run.result !== null && fitCell !== undefined && (
            <div className="card">
              <h2>Fit Summary</h2>
              {run.stale && (
                <p className="sub">
                  Settings changed since this run. Run the model again to
                  update these figures.
                </p>
              )}
              <div className="kv" style={{ marginTop: 0 }}>
                <div>
                  <b>
                    {fitCell.fit !== undefined
                      ? fmtNumber(fitCell.fit.sigma2v, 3)
                      : '—'}
                  </b>
                  <span>Variance component A</span>
                </div>
                <div>
                  <b>
                    {gammas.length > 0
                      ? `${fmtNumber(Math.min(...gammas))} – ${fmtNumber(Math.max(...gammas))}`
                      : '—'}
                  </b>
                  <span>Shrinkage γ range</span>
                </div>
                <div>
                  <b>
                    {fitCell.fit !== undefined
                      ? fmtPct(fitCell.fit.diagnostics.medianCvRatio - 1)
                      : '—'}
                  </b>
                  <span>Median CV vs. Direct</span>
                </div>
                <div>
                  <b>
                    {
                      fitDomains.filter((d) => d.status !== 'publish').length
                    }{' '}
                    of {fitDomains.length}
                  </b>
                  <span>Areas Flagged or Suppressed</span>
                </div>
              </div>
              <p className="sub">
                Model CV for the median area:{' '}
                {fmtCv(
                  fitDomains
                    .map((d) => d.reliability.cv)
                    .filter((v): v is number => v !== undefined)
                    .sort((a, b) => a - b)[Math.floor(fitDomains.length / 2)],
                )}
                . Direct estimates are beside the model estimates in Step 3.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
